import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  alcoholMlForServings,
  alcoholPerCalorieGainPct,
  calorieSavingsPct,
  formatMetric,
  formatMoney,
  formatPercent,
  mlToFlOz,
  priceSavingsPct,
  savingsWording,
  servingsToMatchAlcohol,
  totalCaloriesForServings,
} from '@/lib/metrics';
import type { CatalogRow } from '@/lib/catalog';
import { formatDrinkDisplayName } from '@/lib/displayName';
import { formatPackagePrice } from '@/lib/packageLabel';
import { isBetterOnBoth, type RankableDrink } from '@/lib/recommendations';
import { useCatalog } from '@/hooks/CatalogContext';

function toRankable(row: CatalogRow): RankableDrink {
  return {
    id: row.product.id,
    name: formatDrinkDisplayName(row.product.name),
    category: row.product.category,
    subtype: row.product.subtype,
    alcoholPer100Calories: row.alcoholPer100Calories,
    alcoholPerDollar: row.alcoholPerDollar,
    caloriesPerStandardDrink: row.caloriesPerStandardDrink,
    dollarsPerStandardDrink: row.dollarsPerStandardDrink,
  };
}

/** Matched alcohol servings — keep fractional precision for correct totals. */
function roundMatchedServings(n: number): number {
  return Math.round(n * 1000) / 1000;
}

/**
 * Spinner (step=1) from a fractional matched value should jump to the next
 * whole serving (1.25 → 2 or 1), not add/subtract 1 and land on 2.25.
 */
function snapStepperServings(prev: number, next: number): number {
  if (!Number.isFinite(next) || next < 0) return prev;
  const prevWhole = Math.abs(prev - Math.round(prev)) < 1e-9;
  if (prevWhole) return Math.max(0, next);
  if (next > prev) return Math.ceil(prev - 1e-9);
  if (next < prev) return Math.max(0, Math.floor(prev + 1e-9));
  return next;
}

export function ComparePanel({
  rows,
  onEditPrice,
}: {
  rows: CatalogRow[];
  onEditPrice: (id: string) => void;
}) {
  const { prefs } = useCatalog();
  const idsKey = rows.map((r) => r.product.id).join(',');
  const [servingsById, setServingsById] = useState<Record<string, number>>({});
  const [anchorId, setAnchorId] = useState<string | null>(null);
  /** Draft text while a servings field is focused — allows empty/cleared input on mobile. */
  const [draftById, setDraftById] = useState<Record<string, string>>({});
  const [focusedId, setFocusedId] = useState<string | null>(null);

  useEffect(() => {
    setServingsById((prev) => {
      const next: Record<string, number> = {};
      for (const row of rows) {
        next[row.product.id] = prev[row.product.id] ?? 1;
      }
      return next;
    });
    setDraftById({});
    setFocusedId(null);
    setAnchorId((prev) =>
      prev && rows.some((r) => r.product.id === prev)
        ? prev
        : (rows[0]?.product.id ?? null),
    );
    // Reset when the compare set changes; intentionally not tracking `rows` object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  const matchRows = useMemo(() => {
    return rows.map((row) => {
      const servings = servingsById[row.product.id] ?? 1;
      const servingMl = row.nutrition?.servingMl ?? 0;
      const abv = row.product.abvPercent;
      const alcoholMl =
        abv != null
          ? alcoholMlForServings(servings, servingMl, abv)
          : null;
      const calories = totalCaloriesForServings(
        servings,
        row.nutrition?.calories,
      );
      const servingPrice = row.package.servingPriceUsd;
      const totalPrice =
        servingPrice != null && servings >= 0 ? servings * servingPrice : null;
      return { row, servings, alcoholMl, calories, totalPrice };
    });
  }, [rows, servingsById]);

  const anchorAlcoholMl = useMemo(() => {
    const a = matchRows.find((m) => m.row.product.id === anchorId);
    return a?.alcoholMl ?? null;
  }, [matchRows, anchorId]);

  function commitServings(productId: string, raw: string, opts?: { fromStepper?: boolean }) {
    const trimmed = raw.trim();
    if (trimmed === '' || trimmed === '.' || trimmed === '-') {
      return false;
    }
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed) || parsed < 0) return false;
    const prevValue = servingsById[productId] ?? 1;
    const value = opts?.fromStepper
      ? snapStepperServings(prevValue, parsed)
      : Math.max(0, roundMatchedServings(parsed));
    const source = rows.find((r) => r.product.id === productId);
    if (!source) return false;
    const servingMl = source.nutrition?.servingMl ?? 0;
    const abv = source.product.abvPercent;
    if (abv == null || !(servingMl > 0) || !(abv > 0)) {
      setAnchorId(productId);
      setServingsById((prev) => ({ ...prev, [productId]: value }));
      return true;
    }
    const targetAlcohol = alcoholMlForServings(value, servingMl, abv);
    if (targetAlcohol == null) return false;

    setAnchorId(productId);
    setServingsById((prev) => {
      const next: Record<string, number> = { ...prev, [productId]: value };
      for (const row of rows) {
        if (row.product.id === productId) continue;
        const otherMl = row.nutrition?.servingMl ?? 0;
        const otherAbv = row.product.abvPercent;
        if (otherAbv == null || !(otherMl > 0) || !(otherAbv > 0)) continue;
        const matched = servingsToMatchAlcohol(targetAlcohol, otherMl, otherAbv);
        if (matched != null) next[row.product.id] = roundMatchedServings(matched);
      }
      return next;
    });
    return true;
  }

  function onServingsDraftChange(productId: string, raw: string) {
    // Allow empty / partial input while typing (mobile clear + retype).
    if (raw !== '' && !/^\d*\.?\d*$/.test(raw)) return;
    setDraftById((prev) => ({ ...prev, [productId]: raw }));
    if (raw.trim() === '' || raw === '.') return;
    commitServings(productId, raw);
  }

  function onServingsBlur(productId: string) {
    const draft = draftById[productId];
    if (draft != null) {
      const ok = commitServings(productId, draft);
      if (!ok) {
        // Empty/invalid — restore last committed value (never leave stuck on 0).
        setDraftById((prev) => {
          const next = { ...prev };
          delete next[productId];
          return next;
        });
      } else {
        setDraftById((prev) => {
          const next = { ...prev };
          delete next[productId];
          return next;
        });
      }
    }
    setFocusedId((id) => (id === productId ? null : id));
  }

  function onServingsStep(productId: string, direction: 1 | -1) {
    const prev = servingsById[productId] ?? 1;
    const next = snapStepperServings(prev, prev + direction);
    commitServings(productId, String(next), { fromStepper: true });
    setDraftById((d) => {
      const n = { ...d };
      delete n[productId];
      return n;
    });
  }

  if (rows.length === 0) {
    return (
      <p className="muted">
        Select drinks from the <Link to="/">catalog</Link> to compare (up to 4).
      </p>
    );
  }
  const base = rows[0]!;

  return (
    <div className="compare-stack">
      {rows.length >= 2 && (
        <div className="panel compare-match">
          <h2 className="compare-match__title">Match alcohol by servings</h2>
          <p className="muted">
            Change servings on any drink. The others adjust so each delivers the
            same amount of alcohol; totals show calories (and price when known)
            for that matched amount.
          </p>
          {anchorAlcoholMl != null && (
            <p className="compare-match__target">
              Target alcohol:{' '}
              <strong>{formatMetric(mlToFlOz(anchorAlcoholMl), 3)} fl oz</strong>
              {' · '}
              <strong>{formatMetric(anchorAlcoholMl, 1)} mL</strong>
              {anchorId && (
                <>
                  {' '}
                  (from{' '}
                  {formatDrinkDisplayName(
                    rows.find((r) => r.product.id === anchorId)?.product.name ??
                      'selection',
                  )}
                  )
                </>
              )}
            </p>
          )}
        </div>
      )}

      <div className="compare-grid">
        {matchRows.map(({ row, servings, alcoholMl, calories, totalPrice }) => {
          const calGain =
            row.product.id === base.product.id
              ? null
              : alcoholPerCalorieGainPct(
                  row.alcoholPer100Calories ?? NaN,
                  base.alcoholPer100Calories ?? NaN,
                );
          const canMatch =
            row.product.abvPercent != null &&
            row.product.abvPercent > 0 &&
            (row.nutrition?.servingMl ?? 0) > 0;

          return (
            <article key={row.product.id} className="panel">
              <h2 style={{ fontSize: '1.1rem' }}>
                <Link to={`/drinks/${row.product.slug}/`}>
                  {formatDrinkDisplayName(row.product.name)}
                </Link>
              </h2>
              <p className="muted">
                {row.product.abvPercent ?? '—'}% ABV ·{' '}
                {row.nutrition?.calories ?? '—'} cal / serving
                {row.nutrition?.servingMl
                  ? ` · ${formatMetric(mlToFlOz(row.nutrition.servingMl), 1)} fl oz serving`
                  : ''}
              </p>

              {rows.length >= 2 && (
                <label className="compare-servings">
                  Servings
                  <span className="compare-servings__controls">
                    <button
                      type="button"
                      className="compare-servings__step"
                      disabled={!canMatch}
                      aria-label={`Decrease servings of ${formatDrinkDisplayName(row.product.name)}`}
                      onClick={() => onServingsStep(row.product.id, -1)}
                    >
                      −
                    </button>
                    <input
                      className="select compare-servings__input"
                      type="text"
                      inputMode="decimal"
                      enterKeyHint="done"
                      autoComplete="off"
                      disabled={!canMatch}
                      value={
                        focusedId === row.product.id &&
                        draftById[row.product.id] != null
                          ? draftById[row.product.id]
                          : Number.isFinite(servings)
                            ? String(servings)
                            : '1'
                      }
                      onFocus={(e) => {
                        setFocusedId(row.product.id);
                        setDraftById((prev) => ({
                          ...prev,
                          [row.product.id]: String(
                            Number.isFinite(servings) ? servings : 1,
                          ),
                        }));
                        e.currentTarget.select();
                      }}
                      onChange={(e) =>
                        onServingsDraftChange(row.product.id, e.target.value)
                      }
                      onBlur={() => onServingsBlur(row.product.id)}
                      aria-label={`Servings of ${formatDrinkDisplayName(row.product.name)}`}
                    />
                    <button
                      type="button"
                      className="compare-servings__step"
                      disabled={!canMatch}
                      aria-label={`Increase servings of ${formatDrinkDisplayName(row.product.name)}`}
                      onClick={() => onServingsStep(row.product.id, 1)}
                    >
                      +
                    </button>
                  </span>
                </label>
              )}

              {rows.length >= 2 && canMatch && (
                <ul className="compare-match-stats">
                  <li>
                    Matched alcohol:{' '}
                    <strong>
                      {alcoholMl != null
                        ? `${formatMetric(mlToFlOz(alcoholMl), 3)} fl oz`
                        : '—'}
                    </strong>
                  </li>
                  <li>
                    Total calories:{' '}
                    <strong>
                      {calories != null ? formatMetric(calories, 0) : '—'} cal
                    </strong>
                  </li>
                  <li>
                    Total price:{' '}
                    <strong>{formatMoney(totalPrice)}</strong>
                  </li>
                </ul>
              )}

              <ul>
                <li>
                  Alcohol (
                  {prefs.volumeUnit === 'fl_oz' ? 'fl oz' : 'mL'}) / 100 cal:{' '}
                  <strong>
                    {formatMetric(
                      prefs.volumeUnit === 'fl_oz'
                        ? row.serving.alcoholPer100CaloriesFlOz
                        : row.alcoholPer100Calories,
                    )}
                  </strong>
                </li>
                <li>
                  Alcohol (
                  {prefs.volumeUnit === 'fl_oz' ? 'fl oz' : 'mL'}) / ${' '}
                  <strong>
                    {formatMetric(
                      prefs.volumeUnit === 'fl_oz'
                        ? row.package.alcoholPerDollarFlOz
                        : row.alcoholPerDollar,
                    )}
                  </strong>
                </li>
                <li>
                  cal / standard drink:{' '}
                  <strong>{formatMetric(row.caloriesPerStandardDrink, 1)}</strong>
                </li>
                <li>
                  $ / standard drink:{' '}
                  <strong>
                    {row.dollarsPerStandardDrink != null
                      ? formatMoney(row.dollarsPerStandardDrink)
                      : '—'}
                  </strong>
                </li>
                <li>
                  Price:{' '}
                  <span
                    className={`badge ${row.price.isUserOverride ? 'badge--user' : ''}`}
                  >
                    {row.price.label}
                  </span>
                  {' · '}
                  {formatPackagePrice(
                    row.price.packagePriceUsd,
                    row.price.containerCount,
                    row.price.containerMl,
                    {
                      category: row.product.category,
                      volumeUnit: prefs.volumeUnit,
                    },
                  )}
                </li>
              </ul>
              {row.product.id !== base.product.id && (
                <p>
                  vs {formatDrinkDisplayName(base.product.name)}:{' '}
                  {formatPercent(calGain)} alcohol/calorie
                  ·{' '}
                  {savingsWording(
                    calorieSavingsPct(
                      row.caloriesPerStandardDrink ?? NaN,
                      base.caloriesPerStandardDrink ?? NaN,
                    ),
                    'calories',
                  )}{' '}
                  ·{' '}
                  {savingsWording(
                    priceSavingsPct(
                      row.dollarsPerStandardDrink ?? NaN,
                      base.dollarsPerStandardDrink ?? NaN,
                    ),
                    'price',
                  )}
                  {isBetterOnBoth(toRankable(row), toRankable(base))
                    ? ' · better on both (calories & cost per standard drink)'
                    : ''}
                </p>
              )}
              <div className="row-actions">
                <button
                  type="button"
                  className="btn"
                  onClick={() => onEditPrice(row.product.id)}
                >
                  Edit price
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
